/**
 * Digital bonus product manager.
 *
 * Reconciles a hidden, unlisted bonus product every time the cart sections
 * are re-rendered. The bonus variant id is supplied through
 * `data-bonus-variant-id` on the rendered <digital-bonus> element.
 *
 * Rules:
 *   - At least one product tagged "digital" in cart AND zero non-digital
 *     products → the bonus product is kept in cart with quantity 1.
 *   - Otherwise (no digital items, or any non-digital item present) →
 *     the bonus product is removed from cart.
 *   - The bonus line is always forced to quantity 1 when present.
 *
 * The element re-mounts every time the cart sections are re-rendered (the
 * parent innerHTML is replaced), so reconciliation runs in
 * `connectedCallback`. When the desired state already matches the current
 * cart, no network request is made, which keeps this safe against feedback
 * loops; a global re-entrancy flag protects against concurrent updates.
 */
class DigitalBonus extends HTMLElement {
  constructor() {
    super();
    this.isReconciling = false;
  }

  connectedCallback() {
    if (window.__digitalBonusReconciling) return;
    this.reconcile();
  }

  get bonusVariantId() {
    return parseInt(this.dataset.bonusVariantId, 10);
  }

  get digitalCount() {
    return parseInt(this.dataset.digitalCount, 10) || 0;
  }

  get nonDigitalCount() {
    return parseInt(this.dataset.nonDigitalCount, 10) || 0;
  }

  get bonusInCart() {
    return parseInt(this.dataset.bonusInCart, 10) || 0;
  }

  get shouldHaveBonus() {
    return this.dataset.shouldHaveBonus === 'true';
  }

  reconcile() {
    if (this.shouldHaveBonus) {
      if (this.bonusInCart !== 1) {
        this.setBonusQuantity(1);
      }
    } else if (this.bonusInCart > 0) {
      this.setBonusQuantity(0);
    }
  }

  async setBonusQuantity(quantity) {
    // Always go through /cart/update.js: it both creates the line if it
    // doesn't exist and zeroes it out when needed, and (unlike /cart/add.js)
    // returns the full cart object — so it's safe to publish straight to the
    // cartUpdate channel without a separate /cart fetch and avoids the
    // transient "undefined cart" state on add.
    const sections = this.getSectionsToUpdate();
    const body = JSON.stringify({
      updates: { [this.bonusVariantId]: quantity },
      sections,
    });

    await this.dispatchCartRequest(`${FoxTheme.routes.cart_update_url}`, body);
  }

  async dispatchCartRequest(url, body) {
    if (window.__digitalBonusReconciling) return;
    window.__digitalBonusReconciling = true;

    try {
      const response = await fetch(url, { ...FoxTheme.utils.fetchConfig(), body });
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[digital-bonus] Cart request failed:', response.status, errorText);
        return;
      }

      const parsedState = await response.json();
      FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: parsedState });
    } catch (error) {
      console.error('[digital-bonus] Failed to update bonus product:', error);
    } finally {
      window.__digitalBonusReconciling = false;
    }
  }

  getSectionsToUpdate() {
    const sections = [];
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections } })
    );
    return sections;
  }
}

if (!customElements.get('digital-bonus')) {
  customElements.define('digital-bonus', DigitalBonus);
}
